import React from "react";
import styled, { css } from "styled-components";
import { DetailsSection } from "../DetailsSection";
import { Text } from "../Text";
import { mediaQueries, style } from "../style";
import { ToggleContentButton } from "./SettingsActionRow";

type ColumnProps = {
  fixedActionNode?: boolean;
  renderContentAsRow?: boolean;
};

const DISCLOSURE_ARROW_SPACING = "120px"; // width of column for disclosure (chevron) ">" icon

const DetailsRowContainer = styled(DetailsSection)`
  padding-bottom: ${style.vars.whitespace.regular};
  margin-top: ${style.vars.whitespace.medium};

  /* remove margin for subsequent rows (ie: all BUT the first) */
  &:not(:first-of-type) {
    margin-top: 0;
  }

  &:not(:last-of-type) {
    margin-bottom: ${style.vars.whitespace.medium};
  }
`;

const DetailsRowInnerWrap = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const TitleWrap = styled.div<ColumnProps>`
  flex: 0 0 100%;
  flex-shrink: 1;
  padding-right: 1%;
  box-sizing: border-box;

  ${({ renderContentAsRow, fixedActionNode }) =>
    renderContentAsRow || fixedActionNode
      ? css`
          flex-basis: 60%;
          max-width: 60%;
        `
      : css`
          flex-basis: calc(100% - ${DISCLOSURE_ARROW_SPACING});
          max-width: calc(100% - ${DISCLOSURE_ARROW_SPACING});
        `}

  ${mediaQueries.mediumMin} {
    ${({ renderContentAsRow }) =>
      !renderContentAsRow &&
      css`
        max-width: 25%;
      `}
  }
`;

const Title = styled(Text).attrs({ type: "h4" })``;

const Content = styled.div<ColumnProps>`
  flex: 0 0 100%;
  flex-wrap: wrap;
  order: 3;

  ${({ renderContentAsRow, fixedActionNode }) =>
    renderContentAsRow || fixedActionNode
      ? css`
          max-width: 100%;
          margin-top: 0.5rem;
        `
      : css`
          max-width: calc(100% - ${DISCLOSURE_ARROW_SPACING});
          margin-top: inherit;
          padding-right: 1%;
        `}

  ${mediaQueries.mediumMin} {
    ${({ renderContentAsRow }) =>
      renderContentAsRow
        ? css`
            flex-basis: 100%;
            max-width: 100%;
          `
        : css`
            max-width: 50%;
            margin-top: inherit;
            order: 2;
          `}
  }
`;

const Action = styled.div<ColumnProps>`
  display: flex;
  flex: 0 0 100%;
  justify-content: flex-end;
  padding-left: 1%;
  order: 2;

  ${mediaQueries.mediumMax} {
    ${({ fixedActionNode, renderContentAsRow }) =>
      renderContentAsRow || fixedActionNode
        ? css`
            max-width: 40%;
            flex-basis: 40%;
          `
        : css`
            position: absolute;
            top: 0;
            right: 0;
            height: 100%;
            width: ${DISCLOSURE_ARROW_SPACING};
            max-width: ${DISCLOSURE_ARROW_SPACING};
            flex-basis: ${DISCLOSURE_ARROW_SPACING};
            margin: auto;
            align-items: ${fixedActionNode ? "flex-start" : "center"};
          `}
  }

  ${mediaQueries.mediumMin} {
    max-width: 25%;
    order: 3;

    ${({ renderContentAsRow }) =>
      renderContentAsRow &&
      css`
        max-width: 40%;
        flex-basis: 40%;
        order: 2;
      `}
  }
`;

type DetailsRowProps = {
  id?: string;
  title: React.ReactNode;
  children: React.ReactNode;
  actionNode?: React.ReactNode;
  fixedActionNode?: boolean;
  renderContentAsRow?: boolean;
  innerRef?: React.MutableRefObject<any>;
};

export const DetailsRow: React.FC<DetailsRowProps> = ({
  id,
  title,
  children,
  actionNode,
  fixedActionNode = false,
  renderContentAsRow = false,
  innerRef,
}) => {
  return (
    <DetailsRowContainer innerRef={innerRef}>
      <DetailsRowInnerWrap>
        <TitleWrap renderContentAsRow={renderContentAsRow} fixedActionNode={fixedActionNode}>
          <Title>{title}</Title>
        </TitleWrap>
        <Content renderContentAsRow={renderContentAsRow} fixedActionNode={fixedActionNode}>
          {children}
        </Content>
        <Action renderContentAsRow={renderContentAsRow} fixedActionNode={fixedActionNode}>
          {actionNode &&
            ((React.isValidElement(actionNode) &&
              actionNode.type === ToggleContentButton &&
              React.cloneElement(actionNode as React.ReactElement, {
                "aria-controls": `details-row-action-${id}`,
              })) ||
              actionNode)}
        </Action>
      </DetailsRowInnerWrap>
    </DetailsRowContainer>
  );
};
